/**
 * WHAT: Completeness and Accuracy (C&A) report comparing collected database users against expected baselines.
 * WHY:  C&A validation is a regulatory requirement that ensures the data collection process captured
 *       all users (completeness) and that privilege levels match expectations (accuracy), providing
 *       assurance that attestation decisions are based on reliable data.
 */
import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api.config';
import { WideModal } from './common';
import './CAReport.css';

/**
 * WHAT: Renders a C&A comparison report for a selected database, showing match/over/under status per user.
 * WHY:  Enables compliance teams to identify data collection gaps or discrepancies before attestation,
 *       ensuring that the user access inventory is trustworthy and meets audit-readiness standards.
 */
const CAReport = () => {
  const [databases, setDatabases] = useState([]);
  const [selectedDatabase, setSelectedDatabase] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });

  useEffect(() => {
    fetchDatabases();
  }, []);

  /* WHAT: Loads the list of available databases from the backend for the database selector dropdown.
     WHY: The user must pick a database before generating a C&A report; auto-selects the first one for convenience. */
  const fetchDatabases = async () => {
    try {
      const response = await fetch(API_CONFIG.getEndpoint('/api/databases/oracle-access/databases/'));
      const data = await response.json();
      setDatabases(data.databases || []);

      // Auto-select first database if available
      if (data.databases && data.databases.length > 0) {
        setSelectedDatabase(data.databases[0].id);
      }
    } catch (err) {
      console.error('Error fetching databases:', err);
      setError('Failed to load databases');
    }
  };

  /* WHAT: Triggers a C&A report generation for the selected database via a POST to the backend.
     WHY: The report compares live source-database counts against UAR application counts to verify data collection accuracy. */
  const generateReport = async () => {
    if (!selectedDatabase) {
      setError('Please select a database');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CONFIG.getEndpoint('/api/databases/ca-report/generate/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ database_id: selectedDatabase }),
      });

      // Parse body first — the backend returns JSON even on error responses.
      let data = null;
      try {
        data = await response.json();
      } catch (_) {
        // non-JSON response
      }

      if (!response.ok) {
        const backendMessage = (data && data.error) || `HTTP ${response.status}`;
        const errorCode = data && data.error_code;
        const titleMap = {
          no_connection: 'No Connection Configured',
          connection_inactive: 'Connection Inactive',
          vault_password_missing: 'Vault Password Missing',
          connection_failed: 'Oracle Connection Failed',
          not_found: 'Database Not Found',
          inactive: 'Database Inactive',
        };
        setErrorModal({
          show: true,
          title: titleMap[errorCode] || 'C&A Report Error',
          message: backendMessage,
        });
        return;
      }

      setReportData(data);
    } catch (err) {
      console.error('Error generating report:', err);
      setErrorModal({
        show: true,
        title: 'C&A Report Error',
        message: err.message || 'Failed to generate C&A report.',
      });
    } finally {
      setLoading(false);
    }
  };

  /* WHAT: Returns a styled icon element for a comparison status (MATCH, OVER, UNDER).
     WHY: Visual indicators help auditors quickly identify which metrics match and which have discrepancies. */
  const getStatusIcon = (status) => {
    switch (status) {
      case 'MATCH':
        return <span className="status-icon match">✓</span>;
      case 'OVER':
        return <span className="status-icon over">↑</span>;
      case 'UNDER':
        return <span className="status-icon under">↓</span>;
      default:
        return <span className="status-icon">—</span>;
    }
  };

  /* WHAT: Returns CSS class name based on whether a metric matches or not.
     WHY: Rows with mismatches get highlighted styling to draw auditor attention to discrepancies. */
  const getStatusClass = (status) => {
    return status === 'MATCH' ? 'match' : 'mismatch';
  };

  /* WHAT: Formats an ISO date string to locale-aware display format.
     WHY: Provides human-readable timestamps for report generation and data collection dates. */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  /* WHAT: Computes the overall percentage of comparison metrics that have MATCH status.
     WHY: A single match rate number gives auditors an at-a-glance assessment of data collection quality. */
  const calculateMatchRate = () => {
    if (!reportData?.comparison) return 0;
    const comparison = reportData.comparison;
    const metrics = Object.keys(comparison);
    const matchCount = metrics.filter(key => comparison[key].status === 'MATCH').length;
    return ((matchCount / metrics.length) * 100).toFixed(1);
  };

  /* WHAT: Renders an expandable row showing which specific items are missing or extra between source and UAR.
     WHY: When a metric doesn't match, auditors need to drill into the exact users/roles/privileges that differ. */
  const renderDetailedAnalysis = (analysis, metricType) => {
    if (!analysis) return null;

    // Determine the type of analysis based on the data structure
    const isUserAnalysis = analysis.missing_in_uar && analysis.missing_in_uar[0]?.account_status !== undefined;
    const isRoleAnalysis = analysis.missing_in_uar && analysis.missing_in_uar[0]?.role_name !== undefined;
    const isSystemPrivAnalysis = analysis.missing_in_uar && analysis.missing_in_uar[0]?.privilege !== undefined && !analysis.missing_in_uar[0]?.object_name;
    const isObjectPrivAnalysis = analysis.missing_in_uar && analysis.missing_in_uar[0]?.object_name !== undefined;

    const getEntityName = () => {
      if (isUserAnalysis) return 'users';
      if (isRoleAnalysis) return 'role assignments';
      if (isSystemPrivAnalysis) return 'system privileges';
      if (isObjectPrivAnalysis) return 'object privileges';
      return 'items';
    };

    return (
      <tr className="detailed-analysis-row">
        <td colSpan="6">
          <div className="detailed-analysis">
            <h4>
              <i className="fas fa-search-plus me-2"></i>
              Detailed Difference Analysis
            </h4>

            {analysis.missing_in_uar && analysis.missing_in_uar.length > 0 && (
              <div className="difference-section missing-section">
                <h5>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Missing in UAR Application ({analysis.missing_count} {getEntityName()})
                </h5>
                <p className="section-description">
                  These {getEntityName()} exist in the Source Database but not in UAR Application:
                </p>
                <table className="difference-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      {isUserAnalysis && (
                        <>
                          <th>Created Date</th>
                          <th>Account Status</th>
                          <th>Type</th>
                          <th>Reason</th>
                        </>
                      )}
                      {isRoleAnalysis && (
                        <>
                          <th>Role Name</th>
                          <th>Admin Option</th>
                          <th>Default Role</th>
                        </>
                      )}
                      {isSystemPrivAnalysis && (
                        <>
                          <th>Privilege</th>
                          <th>Admin Option</th>
                        </>
                      )}
                      {isObjectPrivAnalysis && (
                        <>
                          <th>Owner</th>
                          <th>Object Name</th>
                          <th>Privilege</th>
                          <th>Grantable</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.missing_in_uar.map((item, idx) => (
                      <tr key={idx}>
                        <td className="username-col">{item.username}</td>
                        {isUserAnalysis && (
                          <>
                            <td>{item.created_date ? formatDate(item.created_date) : 'N/A'}</td>
                            <td>
                              <span className={`status-badge ${item.account_status.toLowerCase().replace(/\s+/g, '-')}`}>
                                {item.account_status}
                              </span>
                            </td>
                            <td>{item.oracle_maintained === 'Y' ? 'System' : 'Application'}</td>
                            <td>
                              {item.created_after_collection ? (
                                <span className="reason-badge created-after">
                                  <i className="fas fa-clock me-1"></i>
                                  Created after last collection
                                </span>
                              ) : (
                                <span className="reason-badge excluded">
                                  <i className="fas fa-filter me-1"></i>
                                  May have been excluded during collection
                                </span>
                              )}
                            </td>
                          </>
                        )}
                        {isRoleAnalysis && (
                          <>
                            <td className="username-col">{item.role_name}</td>
                            <td>{item.admin_option || 'NO'}</td>
                            <td>{item.default_role || 'NO'}</td>
                          </>
                        )}
                        {isSystemPrivAnalysis && (
                          <>
                            <td className="username-col">{item.privilege}</td>
                            <td>{item.admin_option || 'NO'}</td>
                          </>
                        )}
                        {isObjectPrivAnalysis && (
                          <>
                            <td>{item.owner}</td>
                            <td className="username-col">{item.object_name}</td>
                            <td>{item.privilege}</td>
                            <td>{item.grantable || 'NO'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {analysis.extra_in_uar && analysis.extra_in_uar.length > 0 && (
              <div className="difference-section extra-section">
                <h5>
                  <i className="fas fa-question-circle me-2"></i>
                  Extra in UAR Application ({analysis.extra_count} {getEntityName()})
                </h5>
                <p className="section-description">
                  These {getEntityName()} exist in UAR Application but not in Source Database (possibly revoked or deleted):
                </p>
                <table className="difference-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      {isUserAnalysis && (
                        <>
                          <th>Created Date</th>
                          <th>Account Status</th>
                          <th>Type</th>
                        </>
                      )}
                      {isRoleAnalysis && (
                        <>
                          <th>Role Name</th>
                          <th>Admin Option</th>
                          <th>Default Role</th>
                        </>
                      )}
                      {isSystemPrivAnalysis && (
                        <>
                          <th>Privilege</th>
                          <th>Admin Option</th>
                        </>
                      )}
                      {isObjectPrivAnalysis && (
                        <>
                          <th>Owner</th>
                          <th>Object Name</th>
                          <th>Privilege</th>
                          <th>Grantable</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.extra_in_uar.map((item, idx) => (
                      <tr key={idx}>
                        <td className="username-col">{item.username}</td>
                        {isUserAnalysis && (
                          <>
                            <td>{item.created_date ? formatDate(item.created_date) : 'N/A'}</td>
                            <td>
                              <span className={`status-badge ${item.account_status.toLowerCase().replace(/\s+/g, '-')}`}>
                                {item.account_status}
                              </span>
                            </td>
                            <td>{item.oracle_maintained === 'Y' ? 'System' : 'Application'}</td>
                          </>
                        )}
                        {isRoleAnalysis && (
                          <>
                            <td className="username-col">{item.role_name}</td>
                            <td>{item.admin_option || 'NO'}</td>
                            <td>{item.default_role || 'NO'}</td>
                          </>
                        )}
                        {isSystemPrivAnalysis && (
                          <>
                            <td className="username-col">{item.privilege}</td>
                            <td>{item.admin_option || 'NO'}</td>
                          </>
                        )}
                        {isObjectPrivAnalysis && (
                          <>
                            <td>{item.owner}</td>
                            <td className="username-col">{item.object_name}</td>
                            <td>{item.privilege}</td>
                            <td>{item.grantable || 'NO'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  /* WHAT: Renders a single comparison metric row with source count, UAR count, difference, match %, and status.
     WHY: Each metric (users, roles, privileges) needs its own row with inline detailed analysis when applicable. */
  const renderMetricRow = (label, metric) => {
    return (
      <>
        <tr key={label} className={getStatusClass(metric.status)}>
          <td className="metric-label">
            {getStatusIcon(metric.status)}
            {label}
          </td>
          <td className="metric-value">{metric.oracle.toLocaleString()}</td>
          <td className="metric-value">{metric.postgresql.toLocaleString()}</td>
          <td className="metric-value">{metric.difference.toLocaleString()}</td>
          <td className="metric-value">{metric.match_percentage.toFixed(2)}%</td>
          <td className={`metric-status status-${metric.status.toLowerCase()}`}>
            {metric.status}
          </td>
        </tr>
        {metric.detailed_analysis && renderDetailedAnalysis(metric.detailed_analysis)}
      </>
    );
  };

  /* WHAT: Renders a breakdown comparing account statuses (OPEN, LOCKED, EXPIRED, etc.) between source and UAR.
     WHY: Account status discrepancies may indicate stale data or missed status changes during collection. */
  const renderAccountStatusComparison = () => {
    if (!reportData?.oracle_source?.account_status_breakdown ||
        !reportData?.postgresql_stored?.account_status_breakdown) {
      return null;
    }

    const oracleStatus = reportData.oracle_source.account_status_breakdown;
    const pgStatus = reportData.postgresql_stored.account_status_breakdown;
    const allStatuses = new Set([...Object.keys(oracleStatus), ...Object.keys(pgStatus)]);

    return (
      <div className="account-status-section">
        <h3>Account Status Breakdown</h3>
        <table className="ca-table">
          <thead>
            <tr>
              <th className="metric-name-col">Account Status</th>
              <th className="number-col">Source Database</th>
              <th className="number-col">UAR Application</th>
              <th className="status-col">Match</th>
            </tr>
          </thead>
          <tbody>
            {Array.from(allStatuses).map(status => {
              const oracleCount = oracleStatus[status] || 0;
              const pgCount = pgStatus[status] || 0;
              const isMatch = oracleCount === pgCount;
              return (
                <tr key={status} className={isMatch ? 'match' : 'mismatch'}>
                  <td className="metric-label">
                    {isMatch ? <span className="status-icon match">✓</span> : <span className="status-icon under">✗</span>}
                    {status}
                  </td>
                  <td className="metric-value">{oracleCount}</td>
                  <td className="metric-value">{pgCount}</td>
                  <td className={`metric-status ${isMatch ? 'match-yes' : 'match-no'}`}>
                    {isMatch ? 'Yes' : 'No'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="ca-report-container">
      <div className="ca-header">
        <h1>Completeness & Accuracy Report</h1>
        <p className="ca-description">
          Validates that data extraction from source Oracle databases is complete and accurate
          by comparing counts between source database and PostgreSQL storage.
        </p>
      </div>

      <div className="ca-controls">
        <div className="control-group">
          <label htmlFor="database-select">Select Database:</label>
          <select
            id="database-select"
            value={selectedDatabase}
            onChange={(e) => setSelectedDatabase(e.target.value)}
            disabled={loading}
          >
            <option value="">-- Select Database --</option>
            {databases.map(db => (
              <option key={db.id} value={db.id}>
                {db.db_name} ({db.instance_name})
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={generateReport}
          disabled={loading || !selectedDatabase}
          className="generate-button"
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      <WideModal
        show={errorModal.show}
        onClose={() => setErrorModal({ show: false, title: '', message: '' })}
        title={errorModal.title}
        headerIcon="fa-exclamation-triangle"
        headerClass="bg-danger text-white"
        maxWidth="600px"
        footer={
          <button
            className="btn btn-secondary"
            onClick={() => setErrorModal({ show: false, title: '', message: '' })}
          >
            Close
          </button>
        }
      >
        <div style={{ padding: '10px 5px', fontSize: '15px', lineHeight: '1.6' }}>
          {errorModal.message}
        </div>
      </WideModal>


      {reportData && (
        <div className="report-content">
          <div className="report-summary">
            <div className="summary-card">
              <h3>Report Information</h3>
              <div className="summary-details">
                <div className="summary-item">
                  <span className="summary-label">Database:</span>
                  <span className="summary-value">{reportData.database_name}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Report Generated:</span>
                  <span className="summary-value">{formatDate(reportData.report_generated_at)}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Last Data Collection:</span>
                  <span className="summary-value">{formatDate(reportData.last_collection_timestamp)}</span>
                </div>
              </div>
            </div>

            <div className="summary-card match-rate">
              <h3>Overall Match Rate</h3>
              <div className="match-rate-value">
                {calculateMatchRate()}%
              </div>
              <div className="match-rate-label">
                {Object.keys(reportData.comparison).filter(k => reportData.comparison[k].status === 'MATCH').length} of {Object.keys(reportData.comparison).length} metrics matched
              </div>
            </div>
          </div>

          {reportData.connection_details && (
            <div className="connection-details-section">
              <h2>
                <i className="fas fa-plug me-2"></i>
                Source Database Connection Verification
              </h2>
              <p className="connection-subtitle">
                Live connection established to source Oracle database for real-time data validation
              </p>
              <div className="connection-grid">
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-check-circle"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Connection Status</span>
                    <span className="connection-value success">Successfully Connected</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-clock"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Connected At</span>
                    <span className="connection-value">{formatDate(reportData.connection_details.connected_at)}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-database"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Database Name</span>
                    <span className="connection-value">{reportData.connection_details.database_name}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-server"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Instance Name</span>
                    <span className="connection-value">{reportData.connection_details.instance_name}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-desktop"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Host Name</span>
                    <span className="connection-value">{reportData.connection_details.host_name}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-code-branch"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Database Version</span>
                    <span className="connection-value">{reportData.connection_details.database_version}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-heartbeat"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Database Status</span>
                    <span className="connection-value status-badge">{reportData.connection_details.database_status}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-user"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Connected As</span>
                    <span className="connection-value">{reportData.connection_details.connected_as_user}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-link"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Connection String</span>
                    <span className="connection-value monospace">{reportData.connection_details.connection_string}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-power-off"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Database Startup</span>
                    <span className="connection-value">{formatDate(reportData.connection_details.database_startup_time)}</span>
                  </div>
                </div>
                <div className="connection-item">
                  <span className="connection-icon">
                    <i className="fas fa-tachometer-alt"></i>
                  </span>
                  <div className="connection-info">
                    <span className="connection-label">Connection Duration</span>
                    <span className="connection-value">{reportData.connection_details.connection_duration_seconds}s</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="comparison-section">
            <h2>User Counts</h2>
            <table className="ca-table">
              <thead>
                <tr>
                  <th className="metric-name-col">Metric</th>
                  <th className="number-col">Source Database</th>
                  <th className="number-col">UAR Application</th>
                  <th className="number-col">Difference</th>
                  <th className="number-col">Match %</th>
                  <th className="status-col">Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.comparison.total_users && renderMetricRow('Total Users', reportData.comparison.total_users)}
                {reportData.comparison.application_users && renderMetricRow('Application Users', reportData.comparison.application_users)}
                {reportData.comparison.system_users && renderMetricRow('System Users', reportData.comparison.system_users)}
              </tbody>
            </table>
          </div>

          <div className="comparison-section">
            <h2>Role Assignments</h2>
            <table className="ca-table">
              <thead>
                <tr>
                  <th className="metric-name-col">Metric</th>
                  <th className="number-col">Source Database</th>
                  <th className="number-col">UAR Application</th>
                  <th className="number-col">Difference</th>
                  <th className="number-col">Match %</th>
                  <th className="status-col">Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.comparison.total_role_assignments && renderMetricRow('Direct Role Assignments', reportData.comparison.total_role_assignments)}
              </tbody>
            </table>
          </div>

          <div className="comparison-section">
            <h2>System Privileges</h2>
            <table className="ca-table">
              <thead>
                <tr>
                  <th className="metric-name-col">Metric</th>
                  <th className="number-col">Source Database</th>
                  <th className="number-col">UAR Application</th>
                  <th className="number-col">Difference</th>
                  <th className="number-col">Match %</th>
                  <th className="status-col">Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.comparison.total_system_privileges_direct && renderMetricRow('System Privileges (Direct)', reportData.comparison.total_system_privileges_direct)}
                {reportData.comparison.total_system_privileges_via_roles && renderMetricRow('System Privileges (via Roles)', reportData.comparison.total_system_privileges_via_roles)}
              </tbody>
            </table>
          </div>

          <div className="comparison-section">
            <h2>Object Privileges</h2>
            <table className="ca-table">
              <thead>
                <tr>
                  <th className="metric-name-col">Metric</th>
                  <th className="number-col">Source Database</th>
                  <th className="number-col">UAR Application</th>
                  <th className="number-col">Difference</th>
                  <th className="number-col">Match %</th>
                  <th className="status-col">Status</th>
                </tr>
              </thead>
              <tbody>
                {reportData.comparison.total_object_privileges_direct && renderMetricRow('Object Privileges (Direct)', reportData.comparison.total_object_privileges_direct)}
                {reportData.comparison.total_object_privileges_via_roles && renderMetricRow('Object Privileges (via Roles)', reportData.comparison.total_object_privileges_via_roles)}
              </tbody>
            </table>
          </div>

          {renderAccountStatusComparison()}

          {/* Sample User Details Section */}
          {reportData?.sample_users_oracle && reportData?.sample_users_postgresql && (
            <div className="sample-users-section">
              <h2>
                <i className="fas fa-user-check me-2"></i>
                Sample User Privilege Comparison
              </h2>
              <p className="section-description">
                Detailed privilege breakdown for 5 application users (excluding Oracle-maintained and locked/expired accounts)
              </p>

              {reportData.sample_users_oracle.map((oracleUser, index) => {
                const pgUser = reportData.sample_users_postgresql.find(u => u.username === oracleUser.username);
                if (!pgUser) return null;

                return (
                  <div key={index} className="user-detail-card">
                    <h3 className="user-title">
                      <i className="fas fa-user"></i> {oracleUser.username}
                    </h3>

                    <div className="privilege-comparison-grid">
                      {/* System Privileges Direct */}
                      <div className="privilege-section">
                        <h4><i className="fas fa-shield-alt"></i> System Privileges (Direct)</h4>
                        <div className="privilege-columns">
                          <div className="privilege-column">
                            <div className="column-header">Oracle Source ({oracleUser.system_privileges_direct?.length || 0})</div>
                            <ul className="privilege-list">
                              {oracleUser.system_privileges_direct?.slice(0, 5).map((priv, i) => (
                                <li key={i}>{priv.privilege}</li>
                              ))}
                              {oracleUser.system_privileges_direct?.length > 5 && (
                                <li className="more-items">...and {oracleUser.system_privileges_direct.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                          <div className="privilege-column">
                            <div className="column-header">UAR Application ({pgUser.system_privileges_direct?.length || 0})</div>
                            <ul className="privilege-list">
                              {pgUser.system_privileges_direct?.slice(0, 5).map((priv, i) => (
                                <li key={i}>{priv.privilege}</li>
                              ))}
                              {pgUser.system_privileges_direct?.length > 5 && (
                                <li className="more-items">...and {pgUser.system_privileges_direct.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* System Privileges via Roles */}
                      <div className="privilege-section">
                        <h4><i className="fas fa-users-cog"></i> System Privileges (via Roles)</h4>
                        <div className="privilege-columns">
                          <div className="privilege-column">
                            <div className="column-header">Oracle Source ({oracleUser.system_privileges_via_roles?.length || 0})</div>
                            <ul className="privilege-list">
                              {oracleUser.system_privileges_via_roles?.slice(0, 5).map((priv, i) => (
                                <li key={i}>{priv.privilege}</li>
                              ))}
                              {oracleUser.system_privileges_via_roles?.length > 5 && (
                                <li className="more-items">...and {oracleUser.system_privileges_via_roles.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                          <div className="privilege-column">
                            <div className="column-header">UAR Application ({pgUser.system_privileges_via_roles?.length || 0})</div>
                            <ul className="privilege-list">
                              {pgUser.system_privileges_via_roles?.slice(0, 5).map((priv, i) => (
                                <li key={i}>{priv.privilege}</li>
                              ))}
                              {pgUser.system_privileges_via_roles?.length > 5 && (
                                <li className="more-items">...and {pgUser.system_privileges_via_roles.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Object Privileges Direct */}
                      <div className="privilege-section">
                        <h4><i className="fas fa-database"></i> Object Privileges (Direct) - Sample</h4>
                        <div className="privilege-columns">
                          <div className="privilege-column">
                            <div className="column-header">Oracle Source (showing {Math.min(5, oracleUser.object_privileges_direct?.length || 0)})</div>
                            <ul className="privilege-list">
                              {oracleUser.object_privileges_direct?.slice(0, 5).map((priv, i) => (
                                <li key={i}>{priv.privilege} on {priv.owner}.{priv.object_name}</li>
                              ))}
                              {oracleUser.object_privileges_direct?.length > 5 && (
                                <li className="more-items">...and {oracleUser.object_privileges_direct.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                          <div className="privilege-column">
                            <div className="column-header">UAR Application (showing {Math.min(5, pgUser.object_privileges_direct?.length || 0)})</div>
                            <ul className="privilege-list">
                              {pgUser.object_privileges_direct?.slice(0, 5).map((priv, i) => (
                                <li key={i}>{priv.privilege} on {priv.owner}.{priv.object_name}</li>
                              ))}
                              {pgUser.object_privileges_direct?.length > 5 && (
                                <li className="more-items">...and {pgUser.object_privileges_direct.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Roles Direct */}
                      <div className="privilege-section">
                        <h4><i className="fas fa-user-tag"></i> Direct Roles</h4>
                        <div className="privilege-columns">
                          <div className="privilege-column">
                            <div className="column-header">Oracle Source ({oracleUser.roles_direct?.length || 0})</div>
                            <ul className="privilege-list">
                              {oracleUser.roles_direct?.slice(0, 5).map((role, i) => (
                                <li key={i}>{role.role_name}</li>
                              ))}
                              {oracleUser.roles_direct?.length > 5 && (
                                <li className="more-items">...and {oracleUser.roles_direct.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                          <div className="privilege-column">
                            <div className="column-header">UAR Application ({pgUser.roles_direct?.length || 0})</div>
                            <ul className="privilege-list">
                              {pgUser.roles_direct?.slice(0, 5).map((role, i) => (
                                <li key={i}>{role.role_name}</li>
                              ))}
                              {pgUser.roles_direct?.length > 5 && (
                                <li className="more-items">...and {pgUser.roles_direct.length - 5} more</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* SQL Query Documentation Section */}
          {reportData?.query_documentation && (
            <div className="query-documentation-section">
              <h2>
                <i className="fas fa-database me-2"></i>
                SQL Query Documentation for Auditors
              </h2>
              <p className="section-description">
                Complete list of SQL queries used to extract and validate data for this Completeness & Accuracy Report.
                All queries are documented with their purpose and description for auditor transparency.
              </p>

              {/* Group by database type */}
              {['Oracle', 'PostgreSQL'].map((dbType) => {
                const queries = reportData.query_documentation.filter(q => q.database_type === dbType);
                if (queries.length === 0) return null;

                return (
                  <div key={dbType} className="query-group">
                    <h3 className="query-group-title">
                      <i className={`fas fa-${dbType === 'Oracle' ? 'server' : 'elephant'} me-2`}></i>
                      {dbType === 'Oracle' ? 'Source Database (Oracle)' : 'UAR Application (PostgreSQL)'} Queries
                    </h3>

                    {queries.map((query, index) => (
                      <div key={index} className="query-card">
                        <div className="query-header">
                          <div className="query-badge">{query.category}</div>
                          <h4 className="query-metric">{query.metric}</h4>
                        </div>
                        <div className="query-description">
                          <strong>Description:</strong> {query.description}
                        </div>
                        {query.purpose && (
                          <div className="query-purpose">
                            <strong>Purpose:</strong> {query.purpose}
                          </div>
                        )}
                        <div className="query-sql">
                          <div className="query-sql-header">
                            <i className="fas fa-code me-2"></i>
                            SQL Query
                          </div>
                          <pre className="query-sql-code">{query.sql_query}</pre>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          <div className="legend">
            <h3>Legend</h3>
            <div className="legend-items">
              <div className="legend-item">
                <span className="status-icon match">✓</span> MATCH - Source Database and UAR Application counts are identical
              </div>
              <div className="legend-item">
                <span className="status-icon over">↑</span> OVER - UAR Application has more records than Source Database (unexpected)
              </div>
              <div className="legend-item">
                <span className="status-icon under">↓</span> UNDER - UAR Application has fewer records than Source Database (data may be missing)
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CAReport;
